import { FC, useEffect, useRef } from 'react';
import { IQuestionConfig } from '../core/IQuestion';
import { MiniWizardViewModel } from '../core/MiniWizardViewModel';
import { IGenericQuestionProvider, IQuestionViewProps } from '../core/question-provider/IQuestionProvider';
import { Step } from '../../Step';
import { TextField } from '../../../components/TextField';
import { IContactMethod } from '../../../caremap/ICareMap';
import { IPhoneContactMethod, PhoneField } from '../../../contact-methods/PhoneField';
import { isPlatform } from '@ionic/react';

const questionType = 'contact-info' as const;
type IQuestionType = typeof questionType;

type IContactInfoQuestionProps = {

};

export type IContactInfoQuestionAnswer = {
    address?: string;
    mobilePhone?: IPhoneContactMethod;
    workPhone?: IPhoneContactMethod;
    isMobilePhoneValid?: boolean;
};

export type IContactInfoQuestion = IQuestionConfig<IQuestionType, IContactInfoQuestionAnswer, IContactInfoQuestionProps>;

export class ContactInfoQuestionProvider implements IGenericQuestionProvider<IContactInfoQuestion> {
    questionType = questionType;

    validate = (question: IContactInfoQuestion) => {
        return Boolean(question.answer?.address && question.answer?.isMobilePhoneValid);
    };

    render = (question: IContactInfoQuestion, wizard: MiniWizardViewModel) => {
        return <ContactInfoQuestionView question={question} wizard={wizard} />;
    };
}

export const ContactInfoQuestionView: FC<IQuestionViewProps<IContactInfoQuestion>> = ({ question, wizard }) => {
    const addressRef = useRef<HTMLIonInputElement>(null);

    useEffect(() => {
        if (isPlatform('desktop') || isPlatform('capacitor')) {
            const timer = setTimeout(() => {
                if (addressRef.current && !question.answer?.address) {
                    addressRef.current.setFocus();
                }
            }, 350);
            return () => clearTimeout(timer);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onAddressInput = (address: string) => {
        wizard.answer({
            ...question.answer,
            address: address
        });
    };

    const onPhoneInput = (m: IContactMethod) => {
        wizard.answer({
            ...question.answer,
            [m.type === 'mobile-phone' ? 'mobilePhone' : 'workPhone']: {
                value: m.value,
                type: m.type
            }
        });
    };

    const isValid = (valid: boolean) => {
        wizard.answer({
            ...question.answer,
            isMobilePhoneValid: valid
        });
    };

    return (
        <Step
            data-id="caregiver-contact-info"
            question={question.questionTitle}
            subTitle={question.subTitle}
        >
            <TextField
                inputRef={addressRef}
                name="address"
                label="Address"
                autocapitalize="words"
                value={question.answer?.address}
                onInput={onAddressInput}
            />
            <PhoneField
                isValid={isValid}
                methodChanged={onPhoneInput}
                method={{type: 'mobile-phone', value: question.answer?.mobilePhone?.value || ''}}
            />
            <PhoneField
                showOptional
                methodChanged={onPhoneInput}
                method={{type: 'work-phone', value: question.answer?.workPhone?.value || ''}}
            />
        </Step>
    );
};
